<style scoped>
/* The create/edit sheet for one saved H3 layer. The trigger lives in the tray
   (its "+ New" button and its pencil rows), so this component is the sheet
   itself: a click-away backdrop under a panel that covers the view's left
   panel — the details panel in the shared tool rail (ts/components/panel/
   ToolRail.vue), whose left panel sits 12px in, 335px wide, 12px under the
   window's top and 12px off the bottom. The sheet takes the same box, so it
   covers exactly what it replaces. The `.control-pane-*` names are the pane's
   own (see ToolsPane.vue) and are re-declared here because a scoped rule never
   reaches into another component's markup. */
.h3-editor {
	position: fixed;
	top: 12px;
	bottom: 12px;
	left: 12px;
	width: 335px;
	z-index: 1040;
}

/* The click-away: a transparent full-viewport sheet under the panel, so a click
   anywhere outside the sheet cancels it and no document listener is needed. */
.h3-editor-backdrop {
	position: fixed;
	inset: 0;
}

.h3-editor-panel {
	position: absolute;
	inset: 0;
	z-index: 1;
	overflow-y: auto;
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
	padding: 8px 10px 12px;
	font-size: 0.8125rem;
}

.control-pane-title {
	font-weight: 600;
	margin-bottom: 6px;
}

.h3-editor-intro {
	margin: 0 0 8px;
	color: #6c757d;
	font-size: 0.75rem;
	line-height: 1.35;
}

/* ── The fields ─────────────────────────────────────── */

.h3-editor-field {
	display: flex;
	flex-direction: column;
	gap: 2px;
	margin-top: 6px;
}

.h3-editor-field-label {
	font-weight: 600;
	color: #495057;
	font-size: 0.75rem;
}

.h3-editor-input,
.h3-editor-select {
	border: 1px solid #ced4da;
	border-radius: 0.25rem;
	padding: 3px 8px;
	font-size: 0.8125rem;
	min-width: 0;
	max-width: 100%;
}

.h3-editor-color {
	width: 100%;
	max-width: 100%;
	height: 22px;
	border: 1px solid #ced4da;
	border-radius: 0.25rem;
	padding: 1px 2px;
	background: #fff;
}

/* ── One variable ───────────────────────────────────── */

.h3-editor-row {
	margin-top: 10px;
	padding-top: 8px;
	border-top: 1px solid #f1f3f5;
}

.h3-editor-row-head {
	display: flex;
	align-items: center;
	gap: 6px;
}

.h3-editor-row-title {
	flex: 1 1 auto;
	font-weight: 600;
	color: #212529;
}

/* The four controls of a variable, wrapping rather than squeezing: "Sum of area
   treated (acres)" is a long word and the rail is narrow. */
.h3-editor-controls {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.h3-editor-controls > .h3-editor-field {
	flex: 1 1 8rem;
	min-width: 8rem;
}

.h3-editor-controls > .h3-editor-field:last-child {
	flex: 0 1 5rem;
	min-width: 5rem;
}

/* The honesty rule, in the pane's own two weights: what the district's data
   shows about a chosen source, and what stopped a save. */
.h3-editor-note {
	margin: 4px 0 0;
	color: #6c757d;
	font-size: 0.6875rem;
	line-height: 1.35;
}

.h3-editor-note--warn {
	color: #b45309;
}

.h3-editor-note--error {
	color: #b02a37;
	font-weight: 600;
}

/* ── The buttons ────────────────────────────────────── */

.control-pane-btn {
	border: 1px solid #ced4da;
	background: #fff;
	border-radius: 0.25rem;
	padding: 2px 8px;
	font-size: 0.75rem;
	line-height: 1.4;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.control-pane-btn:hover {
	background: #f1f3f5;
}

.control-pane-btn.active {
	background: #e7f5ff;
	border-color: #74c0fc;
	color: #1864ab;
}

.control-pane-btn:disabled {
	opacity: 0.55;
	cursor: default;
}

.control-pane-btn--primary {
	background: #1864ab;
	border-color: #1864ab;
	color: #fff;
}

.control-pane-btn--primary:hover:not(:disabled) {
	background: #14538c;
}

.control-pane-btn--danger {
	border-color: #dc3545;
	color: #b02a37;
}

.h3-editor-add {
	margin-top: 8px;
}

.h3-editor-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-top: 12px;
	padding-top: 8px;
	border-top: 1px solid #dee2e6;
}

/* The save lands last, under the fields it commits, and the destructive pair
   sits at the other end so a misplaced click cannot delete a layer. */
.h3-editor-actions .control-pane-btn--primary {
	margin-left: auto;
}
</style>

<template>
	<div class="h3-editor">
		<div class="h3-editor-backdrop" @click="emit('cancel')"></div>
		<div class="h3-editor-panel" role="dialog" aria-label="H3 layer">
			<div class="control-pane-title">
				{{ layer ? `Edit “${layer.name}”` : "New H3 layer" }}
			</div>
			<p class="h3-editor-intro">
				A saved layer draws one to three variables — each a source and a metric.
				One variable draws a single ramp; two draw the bivariate 3×3; three add
				a cluster outline on the third. The layer is the district's, and its
				on/off is yours.
			</p>

			<label class="h3-editor-field">
				<span class="h3-editor-field-label">Name</span>
				<input
					v-model="name"
					class="h3-editor-input"
					type="text"
					placeholder="e.g. Chemical treatments, by area"
					title="The layer's name in the district's list"
				/>
			</label>

			<div
				v-for="(variable, index) in variables"
				:key="index"
				class="h3-editor-row"
			>
				<div class="h3-editor-row-head">
					<span class="h3-editor-row-title">{{ rowTitle(index) }}</span>
					<button
						type="button"
						class="control-pane-btn"
						:disabled="variables.length <= 1"
						:title="
							variables.length <= 1
								? 'A layer draws at least one variable'
								: 'Remove this variable'
						"
						@click="removeVariable(index)"
					>
						Remove
					</button>
				</div>
				<p v-if="index === 2" class="h3-editor-note">
					The outline variable: it draws the dissolved outline of the cells
					whose metric is above zero — a cluster boundary under the two fills,
					never a third colour.
				</p>

				<div class="h3-editor-controls">
					<label class="h3-editor-field">
						<span class="h3-editor-field-label">Source</span>
						<select
							class="h3-editor-select"
							:value="variable.source"
							@change="onSource(index, $event)"
						>
							<optgroup
								v-for="table in SERIES_TABLES"
								:key="table.id"
								:label="table.label"
							>
								<option :value="table.whole">
									{{ catalogueLabel(table.whole) }}
								</option>
								<template v-for="field in table.fields" :key="field.id">
									<option
										v-for="member in field.members"
										:key="member.index"
										:value="member.index"
									>
										{{ catalogueLabel(member.index) }}
									</option>
								</template>
							</optgroup>
							<!-- The fields the catalogue deliberately does not offer, named
							     rather than hidden: a ranking is not a fill. -->
							<optgroup label="Not offered — and why">
								<option
									v-for="(ranking, rankIndex) in SERIES_RANKING_FIELDS"
									:key="rankIndex"
									disabled
								>
									{{ ranking.table }} · {{ ranking.field }} —
									{{ ranking.reason }}
								</option>
							</optgroup>
						</select>
					</label>

					<label class="h3-editor-field">
						<span class="h3-editor-field-label">Metric</span>
						<select
							class="h3-editor-select"
							:value="variable.metric"
							@change="onMetric(index, $event)"
						>
							<option
								v-for="measure in SERIES_MEASURES"
								:key="measure.id"
								:value="measure.id"
								:disabled="metricDisabled(variable, measure.id)"
								:title="
									metricDisabled(variable, measure.id)
										? unmeasurableNote(variable.source)
										: measure.blurb
								"
							>
								{{ metricLabel(measure.id) }}
							</option>
						</select>
					</label>

					<label v-if="variable.metric === 'quantity'" class="h3-editor-field">
						<span class="h3-editor-field-label">Unit</span>
						<select
							v-if="units.length > 0"
							class="h3-editor-select"
							:value="variable.unit"
							@change="onUnit(index, $event)"
						>
							<option v-if="!unitKnown(variable.unit)" value="" disabled>
								Choose a unit
							</option>
							<option v-for="unit in units" :key="unit.unit" :value="unit.unit">
								{{ unit.unit }} ({{ unit.rows.toLocaleString() }} rows)
							</option>
						</select>
						<!-- The honest empty state: the district's rows carry quantities in no
						     unit at all, so the measure has nothing to sum within. -->
						<span v-else class="h3-editor-note h3-editor-note--warn">
							this district's data carries no quantity units
						</span>
					</label>

					<label class="h3-editor-field">
						<span class="h3-editor-field-label">Colour</span>
						<input
							class="h3-editor-color"
							type="color"
							:value="variable.color"
							title="The variable's colour"
							@input="onColor(index, $event)"
						/>
					</label>
				</div>

				<!-- The honesty rule: an empty column is NAMED as empty. The note is the
				     catalogue's (or its column's) own finding about the district's data,
				     quoted verbatim, and it is also the reason a non-measurable source's
				     two sums are refused above. -->
				<p
					v-if="sourceNote(variable.source)"
					class="h3-editor-note h3-editor-note--warn"
				>
					{{ sourceNote(variable.source) }}
				</p>
			</div>

			<button
				type="button"
				class="control-pane-btn h3-editor-add"
				:disabled="variables.length >= H3_MAX_VARIABLES"
				:title="
					variables.length >= H3_MAX_VARIABLES
						? `A layer holds at most ${H3_MAX_VARIABLES} variables: two fills and the outline`
						: 'Add another variable'
				"
				@click="addVariable"
			>
				+ Add variable
			</button>

			<p v-if="validation" class="h3-editor-note h3-editor-note--warn">
				{{ validation }}
			</p>
			<p v-if="error" class="h3-editor-note h3-editor-note--error">
				{{ error }}
			</p>

			<div class="h3-editor-actions">
				<button
					type="button"
					class="control-pane-btn"
					:disabled="saving"
					@click="emit('cancel')"
				>
					Cancel
				</button>
				<button
					v-if="layer"
					type="button"
					class="control-pane-btn control-pane-btn--danger"
					:class="{ active: removeArmed }"
					:disabled="saving"
					:title="
						removeArmed
							? `Delete “${layer.name}” for the whole district, with no undo`
							: `Delete “${layer.name}”`
					"
					@click="confirmRemove"
				>
					{{ removeArmed ? "Confirm delete" : "Delete" }}
				</button>
				<button
					type="button"
					class="control-pane-btn control-pane-btn--primary"
					:disabled="!saveable || saving"
					:title="saveTitle"
					@click="onSave"
				>
					{{ saving ? "Saving…" : layer ? "Save changes" : "Create layer" }}
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
// The one place a layer's definition is DRAFTED. It is pure props-and-events —
// the draft, the confirm state and nothing else are local; the request goes up
// whole to the view's handler, which owns the fetch, the store and the mask.
//
// The catalogue is the vocabulary: a source is a `SERIES_TABLES` option whose
// value is the catalogue index the tile has an arm for, so the sheet cannot
// name a series the tile cannot count, and the fields it does not offer are
// listed with their reason rather than hidden. The metric options come from
// `SERIES_MEASURES` for the same reason — its order and ids are the tile's own
// metric ordinals.
import { computed, ref, watch } from "vue";

import {
	defaultH3Definition,
	H3_MAX_VARIABLES,
	H3_VARIABLE_COLORS,
	validateH3Definition,
} from "@/lib/h3/layer";
import {
	areaUnitLabel,
	SERIES_CATALOGUE,
	SERIES_MEASURES,
	SERIES_RANKING_FIELDS,
	SERIES_TABLES,
	type SeriesMeasureID,
} from "@/lib/h3/series";
import type {
	LabH3Unit,
	MapReviewH3Layer,
	MapReviewH3LayerDefinition,
	MapReviewH3LayerRequest,
	MapReviewH3Metric,
	MapReviewH3Variable,
} from "@/type/api";

interface Props {
	/** The layer being edited, or null while the sheet is making a new one. */
	layer: MapReviewH3Layer | null;
	/** The district's live quantity units (`h3-multi?units=1`, most-used first):
	 * the only units a quantity variable may sum within. */
	units: LabH3Unit[];
	/** The district's resolved area unit — the unit the area measure is summed
	 * in at the database, and therefore the one the sheet names for it. */
	areaUnit: string;
	/** A save is in flight: the sheet's own actions are held. */
	saving: boolean;
	/** The last save's failure, already masked by the view's handler. */
	error: string;
}

interface Emits {
	save: [request: MapReviewH3LayerRequest];
	cancel: [];
	/** Delete the layer being edited, by id (never emitted while creating). */
	remove: [id: number];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

/** The draft's name. Seeded from the layer, trimmed on the way out. */
const name = ref(props.layer?.name ?? "");

/** The draft's variables, in draw order. A copy: the seeded definition is the
 * layer's own JSON, and the sheet must not write through to it. */
const variables = ref<MapReviewH3Variable[]>(seedVariables());

function seedVariables(): MapReviewH3Variable[] {
	const definition = props.layer?.definition ?? defaultH3Definition();
	return definition.variables.map((variable) => ({ ...variable }));
}

// The view swaps the prop when it opens a different layer, so one watcher
// re-seeds the whole draft. The delete confirmation is dropped with it: a
// half-pressed delete must not survive into the next layer's sheet.
watch(
	() => props.layer,
	() => {
		name.value = props.layer?.name ?? "";
		variables.value = seedVariables();
		removeArmed.value = false;
	},
);

/** The definition the save would send: the draft, with `unit` normalised to the
 * contract — a unit is the quantity measure's own constraint, so area (the
 * district's unit) and records both store "". */
const definition = computed<MapReviewH3LayerDefinition>(() => ({
	variables: variables.value.map((variable) => ({
		source: variable.source,
		metric: variable.metric,
		unit: variable.metric === "quantity" ? variable.unit : "",
		color: variable.color,
	})),
}));

/** The library's own verdict, null while the definition is saveable. It is the
 * same rule the backend enforces, so the button cannot offer a request the
 * server would refuse. */
const validation = computed(() => validateH3Definition(definition.value));

const saveable = computed(
	() => name.value.trim() !== "" && validation.value === null,
);

const saveTitle = computed(() => {
	if (props.saving) return "Saving…";
	if (name.value.trim() === "") return "Name the layer first";
	return validation.value ?? "Save this layer";
});

// ── The delete confirmation ────────────────────────────────────────────────
//
// Two clicks rather than a native dialog: the first arms the button in place
// (it relabels), the second commits. Nothing is destroyed by one click, and the
// sheet never leaves the rail.
const removeArmed = ref(false);

function confirmRemove(): void {
	if (!props.layer) return;
	if (!removeArmed.value) {
		removeArmed.value = true;
		return;
	}
	removeArmed.value = false;
	emit("remove", props.layer.id);
}

// ── The draft's own writes ─────────────────────────────────────────────────

function addVariable(): void {
	if (variables.value.length >= H3_MAX_VARIABLES) return;
	const ordinal = variables.value.length;
	variables.value.push({
		// The lab's own slot default: the table of the same ordinal.
		source: SERIES_TABLES[ordinal]?.whole ?? SERIES_TABLES[0].whole,
		metric: "records",
		unit: "",
		color: H3_VARIABLE_COLORS[ordinal] ?? H3_VARIABLE_COLORS[0],
	});
}

function removeVariable(index: number): void {
	if (variables.value.length <= 1) return;
	variables.value.splice(index, 1);
}

/** A source the catalogue does not carry cannot be drawn, so it reads the
 * catalogue's only answer: the first table. */
function catalogueEntry(index: number) {
	return (
		SERIES_CATALOGUE.find((entry) => entry.index === index) ??
		SERIES_CATALOGUE[0]
	);
}

function catalogueLabel(index: number): string {
	return catalogueEntry(index).label;
}

function measurable(index: number): boolean {
	return catalogueEntry(index).measurable;
}

/** What the district's data shows about a source, where that is a finding: the
 * catalogue's own note, or the note on the column the entry splits on (the two
 * sources `MapLabSeriesControls`' own `selectionNote` reads). Quoted verbatim —
 * a column that is empty is NAMED as empty. */
function sourceNote(index: number): string {
	const entry = catalogueEntry(index);
	if (entry.note) return entry.note;
	const field = SERIES_TABLES.flatMap((table) => table.fields).find(
		(candidate) => candidate.id === entry.field,
	);
	return field?.note ?? "";
}

/** The fallback reason for a source the catalogue has no note for: the measure
 * reads the treatment table's own columns, and the others have none. */
function unmeasurableNote(index: number): string {
	return (
		sourceNote(index) ||
		"its rows carry no area or quantity of their own — the measure is the treatment table's own columns"
	);
}

function metricDisabled(
	variable: MapReviewH3Variable,
	metric: MapReviewH3Metric,
): boolean {
	return metric !== "records" && !measurable(variable.source);
}

/** The metric in the operator's words. The sums are named as sums of the two
 * columns they read, and the area sum carries the district's own unit. */
function metricLabel(metric: SeriesMeasureID): string {
	if (metric === "records") return "Records";
	if (metric === "quantity") return "Sum of quantity applied";
	const unit = areaUnitLabel(props.areaUnit);
	return unit ? `Sum of area treated (${unit})` : "Sum of area treated";
}

function rowTitle(index: number): string {
	if (index === 0) return "Variable 1 — the fill";
	if (index === 1) return "Variable 2 — fill B (bivariate)";
	return "Variable 3 — the outline variable";
}

function unitKnown(unit: string): boolean {
	return props.units.some((candidate) => candidate.unit === unit);
}

function onSource(index: number, event: Event): void {
	const source = Number((event.target as HTMLSelectElement).value);
	const variable = variables.value[index];
	if (!variable) return;
	variable.source = source;
	// The two sums are the treatment rows' own columns: a source without them
	// has no area and no quantity to sum, so the metric falls back to the count
	// rather than holding a definition the server would refuse.
	if (!measurable(source) && variable.metric !== "records") {
		variable.metric = "records";
	}
}

function onMetric(index: number, event: Event): void {
	const variable = variables.value[index];
	if (!variable) return;
	variable.metric = (event.target as HTMLSelectElement)
		.value as MapReviewH3Metric;
}

function onUnit(index: number, event: Event): void {
	const variable = variables.value[index];
	if (!variable) return;
	variable.unit = (event.target as HTMLSelectElement).value;
}

function onColor(index: number, event: Event): void {
	const variable = variables.value[index];
	if (!variable) return;
	variable.color = (event.target as HTMLInputElement).value;
}

function onSave(): void {
	if (!saveable.value || props.saving) return;
	emit("save", { name: name.value.trim(), definition: definition.value });
}
</script>
